/**
 * Power Distribution Configuration Types (Frontend)
 * TypeScript interfaces and Zod schemas for the repeating Power Distribution (feeds)
 * section (#996 — Facility Power / Power Distribution)
 *
 * IMPORTANT: Zod schemas must be wrapped in useMemo() for iOS to avoid crashes
 * @see GitLab #996 — Repeating Sections (Facility Power / Power Distribution)
 *
 * Contract note: status/name use the GENERIC repeating-section column names
 * `status`/`name` (NOT `powerDistributionStatus`/`powerDistributionName`),
 * mirroring the Large UPS types.
 *
 * The SHARED site-level Facility Power block and the per-instance Large UPS model
 * are NOT part of this per-instance model — they stay on the parent request /
 * their own section.
 */

import { z } from 'zod';

export type PowerDistributionStatus = 'existing' | 'new';

/**
 * Power Distribution Configuration interface (camelCase — matches API response)
 */
export interface PowerDistributionConfiguration {
  id?: number;
  requestId: number;

  // Existing vs New status — generic repeating-section field (#996)
  status: PowerDistributionStatus;

  // User-defined instance name (auto-named "Power Distribution N" server-side when omitted)
  name?: string;

  locationTagId?: number | null;

  // Per-instance Power Distribution fields (6) — keys mirror the live UI dataField keys
  powerUpsOutputVoltage?: string;
  powerDistributionVoltageToLoad?: string;
  powerOverheadBuswayOrUnderFloor?: string;
  powerBuswayAmperage?: string;
  powerBuswayLengthFt?: number;
  powerTapOfBoxRequirements?: string;

  // #997 Bug 2: per-instance Additional Information note (formerly request-level shared)
  powerAdditionalInformation?: string;

  // Metadata
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Input for creating a new power distribution configuration
 */
export type CreatePowerDistributionConfigurationInput = Omit<
  PowerDistributionConfiguration,
  'id' | 'createdAt' | 'updatedAt'
>;

/**
 * Input for updating a power distribution configuration
 */
export type UpdatePowerDistributionConfigurationInput = Partial<
  Omit<
    PowerDistributionConfiguration,
    'id' | 'requestId' | 'createdAt' | 'updatedAt'
  >
>;

/**
 * Grouped power distribution configurations for the segmented view (existing vs new)
 */
export interface GroupedPowerDistributionConfigurations {
  existing: PowerDistributionConfiguration[];
  new: PowerDistributionConfiguration[];
}

/**
 * API response for power distribution configurations list
 */
export interface PowerDistributionConfigurationsResponse {
  powerDistribution: PowerDistributionConfiguration[];
  total: number;
}

/**
 * Zod schema factory for power distribution configuration validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 *
 * @example
 * const schema = useMemo(() => createPowerDistributionConfigurationSchema(), []);
 */
export const createPowerDistributionConfigurationSchema = () =>
  z.object({
    id: z.number().optional(),
    requestId: z.number(),
    status: z.enum(['existing', 'new']),

    // User-defined instance name
    name: z.string().optional(),

    locationTagId: z.number().int().nullable().optional(),

    // Per-instance fields
    powerUpsOutputVoltage: z.string().optional(),
    powerDistributionVoltageToLoad: z.string().optional(),
    powerOverheadBuswayOrUnderFloor: z.string().optional(),
    powerBuswayAmperage: z.string().optional(),
    powerBuswayLengthFt: z.number().optional(),
    powerTapOfBoxRequirements: z.string().optional(),
    powerAdditionalInformation: z.string().optional(),

    // Metadata
    displayOrder: z.number().min(0),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  });

/**
 * Zod schema factory for create power distribution input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const createPowerDistributionInputSchema = () =>
  createPowerDistributionConfigurationSchema().omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  });

/**
 * Zod schema factory for update power distribution input validation
 * IMPORTANT: Must be wrapped in useMemo() to avoid iOS crashes
 */
export const updatePowerDistributionInputSchema = () =>
  createPowerDistributionConfigurationSchema()
    .omit({
      id: true,
      requestId: true,
      createdAt: true,
      updatedAt: true,
    })
    .partial();

/**
 * Type inference from Zod schemas
 */
export type PowerDistributionConfigurationSchema = z.infer<
  ReturnType<typeof createPowerDistributionConfigurationSchema>
>;
export type CreatePowerDistributionInputSchema = z.infer<
  ReturnType<typeof createPowerDistributionInputSchema>
>;
export type UpdatePowerDistributionInputSchema = z.infer<
  ReturnType<typeof updatePowerDistributionInputSchema>
>;

/** Status options for power distribution instances. */
export const POWER_DISTRIBUTION_STATUS_OPTIONS: PowerDistributionStatus[] = [
  'existing',
  'new',
];

/**
 * Default values for a new power distribution configuration
 */
export const DEFAULT_POWER_DISTRIBUTION_CONFIGURATION: Partial<PowerDistributionConfiguration> =
  {
    status: 'new',
    displayOrder: 0,
  };
